import React, { useState, useCallback, useEffect, useRef } from 'react';

import Header from '../../components/common/Header/Header';
import Footer from '../../components/common/Footer/Footer';

const TestimonialPage = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastClickedVideo, setLastClickedVideo] = useState(null);
  
  // Store references to all iframe players
  const playerRefs = useRef({});
  const overlayRefs = useRef({});

  // API endpoint for fetching testimonial videos
  const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/video-tutorials`;

  // Fetch testimonial videos from database
  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/public/testimonials`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setVideos(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching testimonial videos:', err);
      setError('Failed to load testimonials');
      
      // Fallback to static data if API fails
      setVideos([
        {
          id: 1,
          title: "Success Story - Investment Journey",
          embedId: "bH0S2zyrxVA",
          description: "How systematic investing changed my financial life"
        },
        {
          id: 2,
          title: "Customer Experience - Mutual Funds",
          embedId: "bH0S2zyrxVA",
          description: "My experience with mutual fund investments"
        },
        {
          id: 3,
          title: "Financial Goals Achievement",
          embedId: "bH0S2zyrxVA",
          description: "Reaching my financial goals through proper planning"
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Function to stop all videos except the current one
  const stopOtherVideos = (currentVideoId) => {
    Object.keys(playerRefs.current).forEach(videoId => {
      if (videoId !== currentVideoId.toString() && playerRefs.current[videoId]) {
        try {
          // Method 1: Use postMessage to pause
          playerRefs.current[videoId].contentWindow.postMessage(
            '{"event":"command","func":"pauseVideo","args":""}',
            '*'
          );
          
          // Method 2: Reload iframe to stop video (more aggressive)
          setTimeout(() => {
            if (playerRefs.current[videoId]) {
              const currentSrc = playerRefs.current[videoId].src;
              playerRefs.current[videoId].src = '';
              playerRefs.current[videoId].src = currentSrc;
            }
          }, 100);
          
        } catch (error) {
          console.log('Could not stop video:', videoId);
        }
      }
    });
  };

  // Handle video click/play
  const handleVideoClick = (videoId) => {
    console.log('Video clicked:', videoId);
    
    // Stop other videos when this one is clicked
    stopOtherVideos(videoId);
    setLastClickedVideo(videoId);
    
    // Hide overlay for clicked video
    if (overlayRefs.current[videoId]) {
      overlayRefs.current[videoId].style.display = 'none';
    }
    
    // Show overlays for other videos after a delay
    setTimeout(() => {
      Object.keys(overlayRefs.current).forEach(id => {
        if (id !== videoId.toString() && overlayRefs.current[id]) {
          overlayRefs.current[id].style.display = 'flex';
        }
      });
    }, 1000);
  };

  // Listen for YouTube player messages (backup method)
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== 'https://www.youtube.com') return;
      
      try {
        const data = JSON.parse(event.data);
        
        if (data.event === 'video-progress' && data.info) {
          if (data.info.playerState === 1) { // Playing
            const iframe = event.source.frameElement;
            if (iframe && iframe.dataset.videoId) {
              const videoId = iframe.dataset.videoId;
              if (videoId !== lastClickedVideo) {
                handleVideoClick(videoId);
              }
            }
          }
        }
      } catch (error) {
        // Ignore parsing errors
      }
    };

    window.addEventListener('message', handleMessage);
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [lastClickedVideo]);

  // Fetch testimonials on component mount
  useEffect(() => {
    fetchTestimonials();
  }, []);

  // Memoized video rendering for better performance
  const renderTestimonial = useCallback((video) => (
    <div key={video.id} className="testimonial-card">
      <div className="testimonial-wrapper">
        <iframe
          ref={el => {
            if (el) {
              playerRefs.current[video.id] = el;
              el.dataset.videoId = video.id;
            }
          }}
          src={`https://www.youtube.com/embed/${video.embedId}?enablejsapi=1&origin=${window.location.origin}&rel=0`}
          title={video.title}
          allowFullScreen
          loading="lazy"
          className="testimonial-iframe"
          allow="autoplay; encrypted-media"
        />
        
        {/* Invisible overlay to detect clicks */}
        <div
          ref={el => {
            if (el) {
              overlayRefs.current[video.id] = el;
            }
          }}
          className="video-overlay"
          onClick={() => handleVideoClick(video.id)}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'transparent',
            cursor: 'pointer',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div style={{
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            borderRadius: '50%',
            width: '60px',
            height: '60px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '24px',
            transition: 'all 0.3s ease',
            transform: 'scale(1)',
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.1)';
            e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1)';
            e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
          }}
          >
            ▶
          </div>
        </div>
      </div>
    </div>
  ), []);

  const styles = {
    pageContainer: {
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#f8f9fa'
    },
    testimonialHeader: {
      background: '#1a2332',
      backgroundImage: `
        radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 1px, transparent 1px),
        radial-gradient(circle at 75% 75%, rgba(255,255,255,0.05) 1px, transparent 1px)
      `,
      backgroundSize: '20px 20px, 30px 30px',
      padding: '2rem 0',
      marginBottom: '0',
      position: 'relative',
      overflow: 'hidden',
      borderBottom: '1px solid rgba(255,255,255,0.1)'
    },
    testimonialHeaderContent: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '0 2rem',
      position: 'relative',
      zIndex: 2
    },
    testimonialTitle: {
      fontSize: '2rem',
      fontWeight: '600',
      color: '#ffffff',
      margin: '0',
      textAlign: 'center',
      textShadow: '0 1px 2px rgba(0,0,0,0.1)'
    },
    testimonialSubtitle: {
      fontSize: '1.125rem',
      color: 'rgba(255,255,255,0.8)',
      margin: '1rem 0 0 0',
      textAlign: 'center'
    },
    section: {
      padding: '2rem 0',
      backgroundColor: '#f8f9fa',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif',
      flex: 1
    },
    container: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '0 2rem'
    },
    testimonialGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
      gap: '2rem',
      width: '100%'
    },
    loading: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '300px',
      fontSize: '1.125rem',
      color: '#6b7280'
    },
    error: {
      backgroundColor: '#fee2e2',
      color: '#991b1b',
      padding: '1rem',
      borderRadius: '8px',
      marginBottom: '2rem',
      textAlign: 'center',
      border: '1px solid #fca5a5'
    },
    noTestimonials: {
      textAlign: 'center',
      color: '#6b7280',
      fontSize: '1.125rem',
      padding: '3rem 0'
    },
    illustrationContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: '3rem',
      padding: '0 2rem'
    },
    illustrationLeft: {
      width: '200px',
      height: '150px'
    },
    illustrationRight: {
      width: '200px',
      height: '150px'
    }
  };

  return (
    <>
      <style>{`
        /* Testimonial card styling */
        .testimonial-card {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          will-change: transform;
        }

        .testimonial-wrapper {
          position: relative;
          width: 100%;
          height: 0;
          padding-bottom: 56.25%; /* 16:9 aspect ratio */
          background: #000;
        }

        .testimonial-iframe {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          border: none;
          object-fit: cover;
        }

        .video-overlay {
          pointer-events: auto;
        }

        .testimonial-content {
          padding: 1.5rem;
        }

        .testimonial-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: #1f2937;
          margin: 0 0 0.5rem 0;
          line-height: 1.3;
        }

        .testimonial-description {
          font-size: 0.875rem;
          color: #6b7280;
          margin: 0;
          line-height: 1.4;
        }

        .testimonial-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
        }

        /* Performance optimizations */
        * {
          box-sizing: border-box;
        }

        .testimonial-grid {
          contain: layout;
        }

        /* iOS specific optimizations */
        @supports (-webkit-overflow-scrolling: touch) {
          .testimonial-card {
            -webkit-transform: translateZ(0);
            transform: translateZ(0);
          }
        }

        /* Responsive design */
        @media (max-width: 320px) {
          .responsive-container {
            padding: 0 0.5rem !important;
          }
          .testimonial-grid {
            grid-template-columns: 1fr !important;
            gap: 0.75rem !important;
          }
          .testimonial-title-main {
            font-size: 1.1rem !important;
          }
        }

        @media (min-width: 321px) and (max-width: 375px) {
          .responsive-container {
            padding: 0 0.75rem !important;
          }
          .testimonial-grid {
            grid-template-columns: 1fr !important;
            gap: 1rem !important;
          }
          .testimonial-title-main {
            font-size: 1.2rem !important;
          }
        }

        @media (min-width: 376px) and (max-width: 414px) {
          .responsive-container {
            padding: 0 1rem !important;
          }
          .testimonial-grid {
            grid-template-columns: 1fr !important;
            gap: 1rem !important;
          }
          .testimonial-title-main {
            font-size: 1.3rem !important;
          }
        }

        @media (min-width: 415px) and (max-width: 640px) {
          .responsive-container {
            padding: 0 1.5rem !important;
          }
          .testimonial-grid {
            grid-template-columns: 1fr !important;
            gap: 1.5rem !important;
          }
          .testimonial-title-main {
            font-size: 1.4rem !important;
          }
        }

        @media (min-width: 641px) and (max-width: 768px) {
          .responsive-container {
            padding: 0 1.5rem !important;
          }
          .testimonial-grid {
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)) !important;
            gap: 1.5rem !important;
          }
          .testimonial-title-main {
            font-size: 1.6rem !important;
          }
        }

        @media (min-width: 769px) and (max-width: 1024px) {
          .responsive-container {
            padding: 0 2rem !important;
          }
          .testimonial-grid {
            grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)) !important;
            gap: 2rem !important;
          }
          .testimonial-title-main {
            font-size: 1.8rem !important;
          }
        }

        @media (min-width: 1025px) and (max-width: 1366px) {
          .responsive-container {
            padding: 0 2rem !important;
          }
          .testimonial-grid {
            grid-template-columns: repeat(auto-fit, minmax(340px, 1fr)) !important;
            gap: 2rem !important;
          }
          .testimonial-title-main {
            font-size: 2rem !important;
          }
        }

        @media (min-width: 1367px) {
          .testimonial-grid {
            grid-template-columns: repeat(auto-fit, minmax(350px, 1fr)) !important;
            gap: 2.5rem !important;
          }
        }

        /* Landscape orientation optimizations */
        @media (max-height: 500px) and (orientation: landscape) {
          .testimonial-header {
            padding: 1rem 0 !important;
          }
          .testimonial-title-main {
            font-size: 1.2rem !important;
          }
          .section {
            padding: 1rem 0 !important;
          }
        }

        /* Reduce motion for users who prefer it */
        @media (prefers-reduced-motion: reduce) {
          .testimonial-card {
            transition: none !important;
          }
          .testimonial-card:hover {
            transform: none !important;
          }
        }

        /* Dark mode support */
        @media (prefers-color-scheme: dark) {
          .testimonial-card {
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
          }
          .testimonial-card:hover {
            box-shadow: 0 8px 30px rgba(0, 0, 0, 0.4);
          }
        }

        /* Illustrations responsive */
        @media (max-width: 768px) {
          .illustration-container {
            margin-top: 2rem !important;
            padding: 0 1rem !important;
          }
          .illustration-left, .illustration-right {
            width: 120px !important;
            height: 100px !important;
          }
        }

        @media (max-width: 640px) {
          .illustration-container {
            flex-direction: column !important;
            gap: 1rem !important;
            align-items: center !important;
          }
          .illustration-left, .illustration-right {
            width: 100px !important;
            height: 80px !important;
          }
        }

        /* Optimize for touch devices */
        @media (hover: none) and (pointer: coarse) {
          .testimonial-card:hover {
            transform: none;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          }
        }
      `}</style>
      
      <div style={styles.pageContainer}>
        <Header />
        
        <section style={styles.testimonialHeader} className="testimonial-header">
          <div style={styles.testimonialHeaderContent} className="responsive-container">
            <h1 style={styles.testimonialTitle} className="testimonial-title-main">
              Customer Testimonials
            </h1>
            <p style={styles.testimonialSubtitle}>
              Hear from our satisfied customers about their investment journey
            </p>
          </div>
        </section>
        
        <main>
          <section style={styles.section}>
            <div style={styles.container} className="responsive-container">
              {error && (
                <div style={styles.error}>
                  {error}. Showing fallback content.
                </div>
              )}

              {loading ? (
                <div style={styles.loading}>
                  <div>Loading testimonials...</div>
                </div>
              ) : videos.length === 0 ? (
                <div style={styles.noTestimonials}>
                  No testimonials available at the moment. Check back soon!
                </div>
              ) : (
                <div style={styles.testimonialGrid} className="testimonial-grid">
                  {videos.map(renderTestimonial)}
                </div>
              )}

              <div style={styles.illustrationContainer} className="illustration-container">
                <div style={styles.illustrationLeft} className="illustration-left">
                  <svg width="200" height="150" viewBox="0 0 200 150" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <linearGradient id="testimonialGreen" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#10b981" />
                        <stop offset="100%" stopColor="#059669" />
                      </linearGradient>
                    </defs>
                    
                    <circle cx="60" cy="30" r="12" fill="#fbbf24" />
                    <path d="M 56 24 Q 60 20 64 24 Q 62 22 60 22 Q 58 22 56 24" fill="#92400e" />
                    
                    <path d="M 45 45 Q 50 40 60 43 Q 70 40 75 45 L 77 70 Q 75 75 70 75 L 50 75 Q 45 75 43 70 Z" fill="url(#testimonialGreen)" />
                    
                    <ellipse cx="35" cy="55" rx="8" ry="15" fill="url(#testimonialGreen)" transform="rotate(-20 35 55)" />
                    <ellipse cx="85" cy="52" rx="7" ry="12" fill="url(#testimonialGreen)" transform="rotate(25 85 52)" />
                    
                    <path d="M 50 75 L 70 75 L 72 100 L 65 100 L 63 82 L 57 82 L 55 100 L 48 100 Z" fill="#1e293b" />
                    
                    <ellipse cx="52" cy="105" rx="7" ry="5" fill="#2563eb" />
                    <ellipse cx="68" cy="105" rx="7" ry="5" fill="#2563eb" />
                    
                    <path d="M 85 45 Q 95 40 105 45 L 105 65 Q 102 70 95 68 L 88 65 Z" fill="#f59e0b" />
                    <rect x="87" y="48" width="15" height="8" rx="2" fill="#ffffff" />
                    <circle cx="92" cy="52" r="1" fill="#000" />
                    <circle cx="96" cy="52" r="1" fill="#000" />
                    <path d="M 90 55 Q 94 57 98 55" stroke="#000" strokeWidth="0.5" fill="none" />
                  </svg>
                </div>

                <div style={styles.illustrationRight} className="illustration-right">
                  <svg width="200" height="150" viewBox="0 0 200 150" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <linearGradient id="testimonialPurple" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#8b5cf6" />
                        <stop offset="100%" stopColor="#7c3aed" />
                      </linearGradient>
                    </defs>
                    
                    <circle cx="140" cy="35" r="12" fill="#fbbf24" />
                    <path d="M 136 29 Q 140 25 144 29 Q 142 27 140 27 Q 138 27 136 29" fill="#92400e" />
                    
                    <path d="M 125 50 Q 130 45 140 48 Q 150 45 155 50 L 157 75 Q 155 80 150 80 L 130 80 Q 125 80 123 75 Z" fill="url(#testimonialPurple)" />
                    
                    <ellipse cx="115" cy="60" rx="8" ry="15" fill="url(#testimonialPurple)" transform="rotate(-20 115 60)" />
                    <ellipse cx="165" cy="57" rx="7" ry="12" fill="url(#testimonialPurple)" transform="rotate(25 165 57)" />
                    
                    <path d="M 130 80 L 150 80 L 152 105 L 145 105 L 143 87 L 137 87 L 135 105 L 128 105 Z" fill="#1e293b" />
                    
                    <ellipse cx="132" cy="110" rx="7" ry="5" fill="#2563eb" />
                    <ellipse cx="148" cy="110" rx="7" ry="5" fill="#2563eb" />
                    
                    <path d="M 105 50 Q 95 45 85 50 L 85 70 Q 88 75 95 73 L 102 70 Z" fill="#f59e0b" />
                    <rect x="88" y="53" width="15" height="8" rx="2" fill="#ffffff" />
                    <circle cx="93" cy="57" r="1" fill="#000" />
                    <circle cx="97" cy="57" r="1" fill="#000" />
                    <path d="M 91 60 Q 95 62 99 60" stroke="#000" strokeWidth="0.5" fill="none" />
                  </svg>
                </div>
              </div>
            </div>
          </section>
        </main>
        
        <Footer />
      </div>
    </>
  );
};

export default TestimonialPage;